import { useCallback, useEffect, useRef } from 'react';
import { AzaleaPhoneMockup } from './AzaleaPhoneMockup';
import { HERO_AGE_SLIDES } from './heroAgeSlides';

const LOOP_SLIDES = [...HERO_AGE_SLIDES, ...HERO_AGE_SLIDES];
const AUTO_SCROLL_PX_PER_FRAME = 0.55;
const RESUME_DELAY_MS = 1800;

function AgeSlide({
  slide,
  hidden,
}: {
  slide: (typeof HERO_AGE_SLIDES)[number];
  hidden?: boolean;
}) {
  return (
    <article
      className="azalea-age-slide"
      role="listitem"
      aria-hidden={hidden || undefined}
    >
      <p className="azalea-age-slide-label">{slide.label}</p>
      <AzaleaPhoneMockup
        src={slide.src}
        alt={hidden ? '' : slide.alt}
        size="medium"
        loading="eager"
      />
      <p className="azalea-age-slide-desc">{slide.description}</p>
    </article>
  );
}

export function AzaleaAgeCarousel() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const userInteractingRef = useRef(false);
  const hoverPausedRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const resumeTimerRef = useRef<number | null>(null);

  const normalizeScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const loopWidth = viewport.scrollWidth / 2;
    if (loopWidth <= 0) return;

    if (viewport.scrollLeft >= loopWidth) {
      viewport.scrollLeft -= loopWidth;
    } else if (viewport.scrollLeft < 0) {
      viewport.scrollLeft += loopWidth;
    }
  }, []);

  const tick = useCallback(() => {
    const viewport = viewportRef.current;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (
      viewport &&
      !prefersReducedMotion &&
      !userInteractingRef.current &&
      !hoverPausedRef.current
    ) {
      viewport.scrollLeft += AUTO_SCROLL_PX_PER_FRAME;
      normalizeScroll();
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [normalizeScroll]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (resumeTimerRef.current !== null) window.clearTimeout(resumeTimerRef.current);
    };
  }, [tick]);

  const pauseAutoScroll = () => {
    userInteractingRef.current = true;
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
    }
  };

  const scheduleResume = () => {
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = window.setTimeout(() => {
      userInteractingRef.current = false;
    }, RESUME_DELAY_MS);
  };

  return (
    <div className="azalea-age-carousel-wrap">
      <p className="azalea-age-carousel-heading">Built for every age</p>
      <p className="azalea-sr-only">
        Kids, teens, and adults each get their own look and phrases in Azalea.
      </p>
      <div
        ref={viewportRef}
        className="azalea-age-carousel-viewport"
        role="list"
        aria-label="Azalea age options"
        onScroll={normalizeScroll}
        onTouchStart={pauseAutoScroll}
        onTouchEnd={scheduleResume}
        onTouchCancel={scheduleResume}
        onPointerDown={pauseAutoScroll}
        onPointerUp={scheduleResume}
        onPointerCancel={scheduleResume}
        onMouseEnter={() => {
          hoverPausedRef.current = true;
        }}
        onMouseLeave={() => {
          hoverPausedRef.current = false;
        }}
      >
        <div className="azalea-age-carousel-track">
          {LOOP_SLIDES.map((slide, index) => (
            <AgeSlide
              key={`${slide.id}-${index}`}
              slide={slide}
              hidden={index >= HERO_AGE_SLIDES.length}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
